import {Autopay} from "./pages/autopay/Autopay";
import {AddCard} from "./pages/add-card/AddCard";
import {WidgetChooseCard} from "./pages/choose-card/WidgetChooseCard";
import {Messages} from "../Messages";
import {Router} from "../../stem-core/ui/Router";
import {unwrapArray} from "../../stem-core/base/Utils";
import {iFrameConnection} from "../services/IFrameConnection";
import {IFrameMessages} from "../../blink-sdk/messaging/IFrameMessages";
import {WidgetRoutes} from "./WidgetRoutes";
import {Dispatcher} from "../../stem-core/base/Dispatcher";
import {IndexCollapsed} from "./pages/index-collapsed/IndexCollapsed";
import {AddFunds} from "./pages/add-funds/AddFunds";
import {IndexExpanded} from "./pages/index-expanded/IndexExpanded.jsx";
import {iFramePurchaseHandler} from "../services/IFramePurchaseHandler.js";
import {widgetAnimation} from "./WidgetAnimation.js";
import {authService} from "../../client/connection/services/AuthService.js";
import {AuthToken} from "../../client/connection/AuthToken.js";
import {Toast} from "../ui/Toast.jsx";

// Patched onto stem's Router prototype below, for the widget's own URL handling
declare module "../../stem-core/ui/Router" {
    interface Router {
        setWidgetURL(urlParts: any): void;
    }
}


export class WidgetRouting {
    static get RouteElements() {
        return {
            INDEX_COLLAPSED: IndexCollapsed,
            INDEX_EXPANDED: IndexExpanded,
            ADD_FUNDS: AddFunds,
            AUTOPAY: Autopay,
            ADD_CARD:  AddCard,
            CHOOSE_CARD: WidgetChooseCard,
            INSUFFICIENT_FUNDS: AddFunds,
        };
    }

    static getRouteElement(queryRoute: string) {
        // WTF kind of logic is this
        for (const key in WidgetRoutes) {
            if (WidgetRoutes[key] === queryRoute) {
                return this.RouteElements[key];
            }
        }
        return null;
    }

    static initRoutesLogic() {
        this.initBack();
        this.initSetUrl();

        authService.addListener(AuthToken.EventTypes.LOGOUT, () => Router.changeURL(WidgetRoutes.INDEX_COLLAPSED));

        iFrameConnection.addListener(IFrameMessages.ROUTE_CHANGE, ({route}: {route: string}) => {
            Router.changeURL(route);
            if (route === WidgetRoutes.INSUFFICIENT_FUNDS) {
                Toast.showError(Messages.walletInsufficientFunds);
                iFramePurchaseHandler.remakeLastPaymentAfterFundsAdded();
            }
        });

        const dispatchHeightChange = () => {
            const activeElement = this.getRouteElement(Router.localHistory.slice(-1)[0].slice(1));
            if (activeElement && activeElement.instance && activeElement.instance.dispatchHeightChange) {
                activeElement.instance.dispatchHeightChange();
            }
        };

        const observer = new MutationObserver(() => dispatchHeightChange());
        const config = {childList: true, subtree: true};
        observer.observe(document.body, config);

        Dispatcher.Global.addListener("updatedMaxHeight", () => setTimeout(() => dispatchHeightChange()));
    }

    static initSetUrl() {
        const getRouteElement = (...args: [any]) => this.getRouteElement(...args);
        const dispatchRouteChange = (...args: [any]) => this.dispatchRouteChange(...args);

        Router.prototype.setWidgetURL = function(urlParts) {
            let url;
            if (!urlParts.length) {
                url = WidgetRoutes.INDEX_COLLAPSED;
            } else {
                url = unwrapArray(urlParts)[0];
            }

            const element = getRouteElement(url);
            if (!element) {
                return;
            }

            widgetAnimation.handleAnimation(url === WidgetRoutes.INDEX_COLLAPSED);
            if (url === WidgetRoutes.INDEX_COLLAPSED) {
                IndexExpanded.hide();
            } else {
                IndexExpanded.show();
                IndexCollapsed.hide();
            }

            element.show({testId: url.substring(1)});
            dispatchRouteChange(url);

            const page = this.getPageToRender(urlParts);
            const activePage = this.getActive();

            if (activePage && !activePage.node) {
                this.setActive(page);
            }

            // blur the active element to hide keyboard on mobile
            // we want this to happen only when the route truly changes
            if (url !== Router.localHistory.slice(-1)[0].slice(1) && document.activeElement && (document.activeElement as HTMLElement).blur) {
                (document.activeElement as HTMLElement).blur();
            }

            // TODO @cleanup element.instance is the real active page, at least save it somewhere
            this.dispatch("change", url, element.instance);
        };
    }

    static dispatchRouteChange(desiredUrl?: string) {
        let url = desiredUrl;
        if (!url) {
            url = Router.localHistory.slice(-1)[0].slice(1);
        }
        if (!url) {
            return;
        }

        iFrameConnection.sendToSDK(IFrameMessages.ROUTE_CHANGE, {route: url});
    }

    static initBack() {
        const getRouteElement = (...args: [any]) => this.getRouteElement(...args);

        Router.back = function() {
            const poppedUrl = this.localHistory.pop().slice(1)
            const currentUrl = this.localHistory.slice(-1)[0].slice(1);

            const poppedElement = getRouteElement(poppedUrl);
            if (poppedElement) {
                poppedElement.hide();
            }

            const currentElement = getRouteElement(currentUrl);
            if (currentElement && currentElement.instance && currentElement.instance.dispatchHeightChange) {
                currentElement.instance.dispatchHeightChange();
                Dispatcher.Global.dispatch("preventCardScrollDisplay");
            }

            this.Global.setURL([currentUrl]);
        };
    }
}
